import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  BaseService,
  CentrifugoService,
  GameTimelineStages,
  LeagueApi,
  LeagueCourt,
  Playoff,
  Tournament,
  TournamentApi,
  TournamentGroup,
  TournamentRound,
  TournamentRoundApi,
  TournamentRoundTypes,
  TournamentTypes,
} from '@mtgame/core';
import { LeagueRootService } from '@core/services/league-root.service';
import { Router } from '@angular/router';
import { getStageGroupsRoute, getStagesRoute } from '@config/routes';

const STORE_TOURNAMENT_KEY = 'tournament';
const STORE_ROUNDS_KEY = 'rounds';
const STORE_SELECTED_ROUND_KEY = 'selectedRound';
const STORE_GROUPS_KEY = 'groups';
const STORE_GAMES_STAGES_KEY = 'games_stages';
const STORE_COURTS_KEY = 'courts';
const STORE_PLAYOFFS_KEY = 'playoffs';

@Injectable({providedIn: 'root'})
export class TournamentRootService extends BaseService {
  private tournamentId: number;
  private leagueId: number;

  get tournament$(): Observable<Tournament> {
    return this.store.get(STORE_TOURNAMENT_KEY);
  }

  get rounds$(): Observable<TournamentRound[]> {
    return this.store.get(STORE_ROUNDS_KEY);
  }

  get selectedRound$(): Observable<TournamentRound> {
    return this.store.get(STORE_SELECTED_ROUND_KEY);
  }

  get groups$(): Observable<TournamentGroup[]> {
    return this.store.get(STORE_GROUPS_KEY);
  }

  get gamesStages$(): Observable<GameTimelineStages> {
    return this.store.get(STORE_GAMES_STAGES_KEY);
  }

  get courts$(): Observable<LeagueCourt[]> {
    return this.store.get(STORE_COURTS_KEY);
  }

  get playoffs$(): Observable<Playoff[]> {
    return this.store.get(STORE_PLAYOFFS_KEY);
  }

  constructor(
    private tournamentApi: TournamentApi,
    private leagueApi: LeagueApi,
    private centrifugoService: CentrifugoService,
    private leagueRootService: LeagueRootService,
    private tournamentRoundApi: TournamentRoundApi,
    private router: Router,
  ) {
    super();
  }

  async initialize(alias: string, roundId?: number): Promise<Tournament> {
    const league = this.leagueRootService.getLeagueModel();
    const tournament = await this.tournamentApi.getByAlias(league.id, alias);
    this.store.save(STORE_TOURNAMENT_KEY, tournament);
    this.tournamentId = tournament.id;
    this.leagueId = league.id;
    if (tournament.settings.type === TournamentTypes.fiba) {
      //await this.router.navigate(getStagesRoute(tournament.alias));
    } else {
      const rounds = await this.getRounds();
      let selectedRound: TournamentRound;
      if (roundId) {
        selectedRound = rounds.find(item => item.id === roundId);
      }
      if (selectedRound) {
        await this.setRound(selectedRound);
      } else {
        await this.setRound(rounds[rounds.length - 1]);
      }
      await this.getGamesStages();
    }
    return tournament;
  }

  dispose(): void {
    super.dispose();
    this.centrifugoService.unsubscribe(`tournament_${this.tournamentId}`);
  }

  getTournament(): Tournament {
    return this.store.value(STORE_TOURNAMENT_KEY);
  }

  async getRounds(): Promise<TournamentRound[]> {
    const rounds = await this.tournamentApi.getTournamentRounds(this.tournamentId);
    this.store.save(STORE_ROUNDS_KEY, rounds);
    return rounds;
  }

  async setRound(round: TournamentRound): Promise<void> {
    if (round.type === TournamentRoundTypes.group) {
      await this.getGroups(round.id);
    } else if (round.type === TournamentRoundTypes.playoff) {
      await this.getPlayoffs(round.id);
    }
    this.store.save(STORE_SELECTED_ROUND_KEY, round);
  }

  async getGroups(roundId: number): Promise<TournamentGroup[]> {
    const groups = await this.tournamentRoundApi.getGroups(roundId);
    this.store.save(STORE_GROUPS_KEY, groups);
    return groups;
  }

  async getGamesStages(): Promise<GameTimelineStages> {
    const gamesStages = await this.tournamentApi.getGamesStages(this.tournamentId);
    this.store.save(STORE_GAMES_STAGES_KEY, gamesStages);
    return gamesStages;
  }

  async getCourts(): Promise<LeagueCourt[]> {
    const courts = await this.leagueApi.getCourts(this.leagueId);
    this.store.save(STORE_COURTS_KEY, courts);
    return courts;
  }

  async getPlayoffs(roundId: number): Promise<Playoff[]> {
    const playoffs = await this.tournamentRoundApi.getPlayoffs(roundId);
    this.store.save(STORE_PLAYOFFS_KEY, playoffs);
    return playoffs;
  }
}
